import { FC, useEffect, useState, useMemo } from 'react';
import { BootstrapInput, useStyles } from './styles';
import { useStore } from 'hooks/useRootStore';
import { useQuery } from 'react-query';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { GridColumns, GridCellParams, DataGrid, GridSortDirection } from '@material-ui/data-grid';
import { Grid, Paper, Checkbox, Select, InputLabel, Box, FormControl, MenuItem, ListItemText } from '@material-ui/core';
import { Search } from 'components/Search';
import { observer } from 'mobx-react-lite';

import { capitalizeFirstLetter, toFormatDateTime } from 'utils';
import { getRowsPerPageOptions } from 'utils/pagination';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import Calender from './../../../assets/icons/EndUser/Calender.svg';
import { TabsEditRequestProps } from './types';
import { useIsMobile } from 'hooks/useResponsive';
export const EndUserRequestTable: FC<TabsEditRequestProps> = observer(() => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  useEffect(() => {
    document.title = 'EasyIdentity: Requests for End Users';
  }, []);
  const {
    userEditRequestTableStore: { getIds, getEndUserRequest },
    notificationStore: { openNotification },
  } = useStore();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterState, setFilterState] = useState<string[]>([]);
  const getEndUserRequestReq = useQuery('getEndUserRequestReq', () => getEndUserRequest(), {
    onError: (e) => {
      openNotification('error', `Can't get users request`);
    },
  });
  const data = getEndUserRequestReq?.data?.data?.data;
  const getRenderValueForRequestSelect = () => {
    const renderValues = filterState.map((group: any) => `${capitalizeFirstLetter(group)}`);
    return renderValues.join(', ');
  };
  const rows = useMemo(() => {
    const base: any[] = data || [];
    const search = searchTerm.trim().toLowerCase();
    const statusFiltered = filterState.length > 0 ? base.filter((r) => filterState.includes(r.status)) : base;
    if (!search) return statusFiltered;
    return statusFiltered.filter((r) =>
      [r.name, r.type, r.status, r.requested_at, toFormatDateTime(r.requested_at || '')]
        .join(' ')
        .toLowerCase()
        .includes(search)
    );
  }, [data, searchTerm, filterState]);

  const [pageSize, setPageSize] = useState<number>(10);
  const rowsPerPageOptions = useMemo(() => getRowsPerPageOptions(rows), [rows]);

  const columns: GridColumns = [
    {
      field: 'icon',
      headerName: ' ',
      width: 40,
      align: 'center',
      renderCell: (params: GridCellParams) => <RecordVoiceOverIcon style={{ color: 'grey' }} />,
    },
    {
      field: 'requested_at',
      headerName: 'DATE/TIME',
      width: 220,
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <div style={{ display: 'flex', gap: '10px' }}>
          <img src={Calender} alt='' />
          {toFormatDateTime(params.row.requested_at.toString())}
        </div>
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 1,
      width: 220,
      renderCell: (params: GridCellParams) => {
        const value = String(params.value ?? '');
        return <div style={{ display: 'flex', gap: '10px' }}>{value}</div>;
      },
    },
    { field: 'name', headerName: 'FULL NAME', width: 180, flex: 1 },
    {
      field: 'status',
      headerName: 'STATUS',
      flex: 0.5,
      renderCell: (params: GridCellParams) => (
        <p style={{ color: params.value === 'pending' ? '#FFD700' : params.value === 'approved' ? 'green' : 'red' }}>
          {capitalizeFirstLetter(params.row.status)}
        </p>
      ),
    },
  ];

  useAppPreloader(getEndUserRequestReq.isFetching);

  return (
    <div className={classes.container}>
      <Paper elevation={0}>
        <Grid container justify='space-between' alignItems='center' className={classes.box}>
          <Box style={{ minWidth: 120, marginLeft: '16px' }}>
            <FormControl fullWidth>
              <InputLabel id='demo-simple-select-label'>Select Filter</InputLabel>
              <Select
                input={<BootstrapInput />}
                className={classes.select}
                labelId='demo-simple-select-label'
                id='demo-simple-select'
                value={filterState}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  getContentAnchorEl: null,
                }}
                multiple
                renderValue={() => {
                  if (Array.isArray(filterState) && filterState.length === 0) {
                    return <div>Selected</div>;
                  } else {
                    return <div>{getRenderValueForRequestSelect()}</div>;
                  }
                }}
              >
                {['pending', 'rejected'].map((item) => (
                  <MenuItem key={item} value={item} style={{ background: 'none' }}>
                    <Checkbox
                      className={classes.checkbox}
                      checked={filterState.includes(item)}
                      onChange={() =>
                        setFilterState((prev: string[]) =>
                          prev.includes(item) ? prev.filter((val: string) => val !== item) : [...prev, item]
                        )
                      }
                    />
                    <ListItemText primary={capitalizeFirstLetter(item)} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Search handelSearch={setSearchTerm} />
          </Box>
        </Grid>
        <div
          style={{
            minWidth: isMobile ? 900 : '100%',
            overflowX: 'auto',
          }}
        >
          <DataGrid
            autoHeight
            className={classes.boxStatus}
            getRowClassName={(params) =>
              params.row.read === 1
                ? 'box--read'
                : params.row.read === 0
                ? 'box--unread'
                : getIds.includes(params.row.id)
                ? 'box--rejecting'
                : 'box--pending'
            }
            sortingOrder={['asc', 'desc']}
            sortModel={[
              {
                field: 'requested_at',
                sort: 'desc' as GridSortDirection,
              },
            ]}
            rows={rows}
            columns={columns}
            pageSize={pageSize}
            rowsPerPageOptions={rowsPerPageOptions}
            pagination
            onPageChange={(params) => setPageSize(params.pageSize)}
          />
        </div>
      </Paper>
    </div>
  );
});
